import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import PollAvailabilityGrid from '../components/PollAvailabilityGrid';

/* ─── Interfaces ──────────────────────────────────────────────── */

interface PollSlot {
  slot_id: number;
  slot_date: string;
  am_pm: 'AM' | 'PM';
  vote_count: number;
  my_vote: boolean;
  my_notes: string | null;
  voters?: string[];
}

interface PollTask {
  type: 'poll';
  poll_id: number;
  tournament_id: number;
  tournament_name: string;
  tournament_status: string;
  completed: boolean;
  total_rounds_required: number;
  slots: PollSlot[];
}

interface EventPlayer {
  first_name: string;
  last_name: string;
  is_self?: boolean;
}

interface TournamentRoundEvent {
  type: 'tournament_round';
  sort_key: string;
  tournament_id: number;
  tournament_name: string;
  schedule_status: string;
  round_number: number;
  group_number: number;
  slot_date: string;
  am_pm: 'AM' | 'PM';
  course_name: string | null;
  tee_time: string | null;
  reservation_name: string | null;
  complex_name: string | null;
  reservation_course_name: string | null;
  is_leaderboard_round: boolean;
  is_final_round: boolean;
  group_members: EventPlayer[];
}

interface ReservationEvent {
  type: 'reservation';
  sort_key: string;
  reservation_id: number;
  reservation_date: string;
  tee_time: string | null;
  course_name: string | null;
  reservation_name: string | null;
  complex_name: string | null;
  players: EventPlayer[];
}

type UpcomingEvent = TournamentRoundEvent | ReservationEvent;

/** A scorecard photo waiting for this user's review (see /scorecard-review/:id). */
interface ScorecardTask {
  type: 'scorecard_review';
  upload_id: number;
  round_id: number;
  course_name: string | null;
  round_date: string | null;
  status: string;
  blocking_count: number | null;
  escalated: boolean;
  uploader_name: string | null;
}

type Task = PollTask;

/* ─── Helpers ─────────────────────────────────────────────────── */

function fmtDate(d: string | null | undefined) {
  if (!d) return '—';
  const ds = d.length >= 10 ? d.slice(0, 10) : d;
  const m = ds.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return d;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmtSession(am_pm: 'AM' | 'PM') {
  return am_pm === 'AM' ? 'Morning' : 'Afternoon';
}

/* ─── Main component ──────────────────────────────────────────── */

const MyCareer: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const APIURL = process.env.REACT_APP_API_URL;

  const [tasks, setTasks] = useState<Task[]>([]);
  const [scorecardTasks, setScorecardTasks] = useState<ScorecardTask[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<number | null>(null);
  const [showAllTournaments, setShowAllTournaments] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, { slot_ids: number[]; notes: string }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };

      const [tasksRes, careerRes] = await Promise.all([
        fetch(`${APIURL}/my-tasks`, { headers }),
        fetch(`${APIURL}/my-career`, { headers }),
      ]);

      const tasksJson = await tasksRes.json();
      const careerJson = await careerRes.json();

      const all: (Task | ScorecardTask)[] = tasksJson.tasks || [];
      const fetched = all.filter((t): t is Task => t.type === 'poll');
      setTasks(fetched);
      setScorecardTasks(all.filter((t): t is ScorecardTask => t.type === 'scorecard_review'));
      setUpcomingEvents(careerJson.upcoming_events || []);

      const seeded: Record<number, { slot_ids: number[]; notes: string }> = {};
      for (const t of fetched) {
        if (t.type === 'poll') {
          seeded[t.tournament_id] = {
            slot_ids: t.slots.filter((s) => s.my_vote).map((s) => s.slot_id),
            notes: t.slots.find((s) => s.my_notes)?.my_notes || '',
          };
        }
      }
      setDrafts(seeded);
    } catch (e) {
      console.error('[my-career load]', e);
    } finally {
      setLoading(false);
    }
  }, [APIURL, getAccessTokenSilently]);

  useEffect(() => { load(); }, [load]);

  const submitPollVote = async (tournamentId: number) => {
    const draft = drafts[tournamentId];
    if (!draft) return;
    setSubmitting(tournamentId);
    try {
      const token = await getAccessTokenSilently();
      const res = await fetch(`${APIURL}/tournaments/${tournamentId}/scheduler/poll/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ slot_ids: draft.slot_ids, notes: draft.notes || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to submit availability.');
        return;
      }
      await load();
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) return <div style={s.page}><div style={s.loading}>Loading…</div></div>;

  const visibleTasks = showAllTournaments
    ? tasks
    : tasks.filter((t) => t.tournament_status === 'open');
  const openTasks = visibleTasks.filter((t) => !t.completed);
  const completedTasks = visibleTasks.filter((t) => t.completed);
  const hiddenCount = tasks.length - visibleTasks.length;

  return (
    <div style={s.page}>
      <h2 style={s.title}>My Career</h2>

      {/* ── Upcoming events ─────────────────────────────────────── */}
      <section style={s.section}>
        <h3 style={s.sectionHeader}>Your upcoming events</h3>

        {upcomingEvents.length === 0 ? (
          <div style={s.emptyUpcoming}>No upcoming scheduled rounds yet.</div>
        ) : (
          upcomingEvents.map((event, i) =>
            event.type === 'tournament_round' ? (
              <TournamentRoundCard key={`tr-${event.tournament_id}-${event.round_number}-${i}`} event={event} />
            ) : (
              <ReservationCard key={`res-${event.reservation_id}`} event={event} />
            )
          )
        )}
      </section>

      {/* ── Scorecard photos to review ──────────────────────────── */}
      {scorecardTasks.length > 0 && (
        <section style={s.section}>
          <h3 style={s.sectionHeader}>Scorecards to review</h3>
          {scorecardTasks.map((t) => (
            <Link key={t.upload_id} to={`/scorecard-review/${t.upload_id}`} style={{ ...s.eventCard, display: 'block', textDecoration: 'none', color: 'inherit' }}>
              <div style={s.eventCardTag}>
                📷 SCORECARD{t.escalated ? ` · FROM ${String(t.uploader_name || 'A PLAYER').toUpperCase()} · WAITING` : ''}
              </div>
              <div style={s.eventCardTitle}>{t.course_name || 'Round'} · {fmtDate(t.round_date)}</div>
              <div style={{ fontSize: 13, color: '#6c757d', marginTop: 4 }}>
                {t.status === 'reading' ? 'Reading the card…'
                  : t.status === 'agent_working' ? 'The assistant is checking it…'
                  : t.blocking_count ? `${t.blocking_count} thing${t.blocking_count === 1 ? '' : 's'} to check →`
                  : 'Everything checks out — confirm and save →'}
              </div>
            </Link>
          ))}
        </section>
      )}

      {/* ── Tasks (polls) ───────────────────────────────────────── */}
      <section style={s.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <h3 style={{ ...s.sectionHeader, marginBottom: 0 }}>Tasks</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: '#495057' }}>
            <input type="checkbox" checked={showAllTournaments} onChange={(e) => setShowAllTournaments(e.target.checked)} />
            Show closed tournaments
            {hiddenCount > 0 && !showAllTournaments && (
              <span style={{ color: '#6c757d', fontSize: 12 }}>({hiddenCount} hidden)</span>
            )}
          </label>
        </div>

        {visibleTasks.length === 0 && (
          <div style={s.empty}>
            {tasks.length === 0 ? '🎉 You\'re all caught up. No pending tasks.' : 'No tasks for active tournaments.'}
          </div>
        )}

        {openTasks.length > 0 && (
          <>
            <div style={s.taskGroupLabel}>Open ({openTasks.length})</div>
            {openTasks.map((t) => (
              <PollTaskCard
                key={`${t.type}-${t.poll_id}`}
                task={t}
                draft={drafts[t.tournament_id] || { slot_ids: [], notes: '' }}
                setDraft={(d) => setDrafts({ ...drafts, [t.tournament_id]: d })}
                onSubmit={() => submitPollVote(t.tournament_id)}
                submitting={submitting === t.tournament_id}
              />
            ))}
          </>
        )}

        {completedTasks.length > 0 && (
          <>
            <button
              style={s.collapseToggle}
              onClick={() => setShowCompleted((v) => !v)}
            >
              {showCompleted ? '▼' : '▶'} Completed ({completedTasks.length})
            </button>
            {showCompleted && completedTasks.map((t) => (
              <PollTaskCard
                key={`${t.type}-${t.poll_id}-done`}
                task={t}
                draft={drafts[t.tournament_id] || { slot_ids: [], notes: '' }}
                setDraft={(d) => setDrafts({ ...drafts, [t.tournament_id]: d })}
                onSubmit={() => submitPollVote(t.tournament_id)}
                submitting={submitting === t.tournament_id}
                dim
              />
            ))}
          </>
        )}
      </section>
    </div>
  );
};

/* ─── Individual event cards ──────────────────────────────────── */

const TournamentRoundCard: React.FC<{ event: TournamentRoundEvent }> = ({ event }) => (
  <div style={s.eventCard}>
    <div style={s.eventCardHeader}>
      <div>
        <div style={s.eventCardTag}>TOURNAMENT · ROUND {event.round_number}</div>
        <div style={s.eventCardTitle}>{event.tournament_name}</div>
      </div>
      {event.schedule_status === 'draft' && (
        <span style={s.draftBadge}>Draft</span>
      )}
    </div>

    <div style={s.roundRow}>
      <div style={s.roundDateBlock}>
        <div style={s.roundDate}>{fmtDate(event.slot_date)}</div>
        <span style={{
          ...s.sessionBadge,
          background: event.am_pm === 'AM' ? '#e7f1ff' : '#fff3cd',
          color: event.am_pm === 'AM' ? '#084298' : '#856404',
        }}>
          {fmtSession(event.am_pm)}
        </span>
      </div>

      <div style={s.roundMeta}>
        {event.course_name && <div style={s.courseName}>@ {event.course_name}</div>}
        {event.tee_time ? (
          <div style={s.teeTimeConfirmed}>
            ⏰ {event.tee_time}
            {event.reservation_name ? ` — Reserved under ${event.reservation_name}` : ''}
          </div>
        ) : (
          <div style={s.teeTimeTbd}>Tee time TBD</div>
        )}
        {(event.complex_name || event.reservation_course_name) && (
          <div style={s.reservationVenue}>
            {event.complex_name && <>at {event.complex_name}</>}
            {event.complex_name && event.reservation_course_name && ' · '}
            {event.reservation_course_name && <>{event.reservation_course_name}</>}
          </div>
        )}
        <div style={s.roundFlags}>
          {event.is_leaderboard_round && <span style={s.flag}>🏆 Leaderboard</span>}
          {event.is_final_round && <span style={s.flag}>🏁 Final Round</span>}
        </div>
      </div>

      <div style={s.roundGroupBlock}>
        <div style={s.groupLabel}>Group {event.group_number}</div>
        {event.group_members.map((m, i) => (
          <div key={i} style={m.is_self ? s.groupMemberSelf : s.groupMember}>
            {m.first_name} {m.last_name}{m.is_self ? ' (you)' : ''}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const ReservationCard: React.FC<{ event: ReservationEvent }> = ({ event }) => (
  <div style={s.eventCard}>
    <div style={{ ...s.eventCardHeader, background: '#495057', borderBottomColor: '#343a40' }}>
      <div>
        <div style={s.eventCardTag}>REGULAR ROUND</div>
        <div style={s.eventCardTitle}>
          {event.course_name || 'Tee Time Reservation'}
        </div>
      </div>
    </div>

    <div style={s.roundRow}>
      <div style={s.roundDateBlock}>
        <div style={s.roundDate}>{fmtDate(event.reservation_date)}</div>
      </div>
      <div style={s.roundMeta}>
        {event.course_name && <div style={s.courseName}>@ {event.course_name}</div>}
        {event.tee_time
          ? (
            <div style={s.teeTimeConfirmed}>
              ⏰ {event.tee_time}
              {event.reservation_name ? ` — Reserved under ${event.reservation_name}` : ''}
            </div>
          )
          : <div style={s.teeTimeTbd}>Tee time TBD</div>}
        {event.complex_name && (
          <div style={s.reservationVenue}>at {event.complex_name}</div>
        )}
      </div>
      {event.players && event.players.length > 0 && (
        <div style={s.roundGroupBlock}>
          <div style={s.groupLabel}>Players</div>
          {event.players.map((p, i) => (
            <div key={i} style={p.is_self ? s.groupMemberSelf : s.groupMember}>
              {p.first_name} {p.last_name}{p.is_self ? ' (you)' : ''}
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);

/* ─── Poll task card ──────────────────────────────────────────── */

interface CardProps {
  task: PollTask;
  draft: { slot_ids: number[]; notes: string };
  setDraft: (d: { slot_ids: number[]; notes: string }) => void;
  onSubmit: () => void;
  submitting: boolean;
  dim?: boolean;
}

const PollTaskCard: React.FC<CardProps> = ({ task, draft, setDraft, onSubmit, submitting, dim }) => {
  const toggleSlot = (slotId: number) => {
    const next = draft.slot_ids.includes(slotId)
      ? draft.slot_ids.filter((id) => id !== slotId)
      : [...draft.slot_ids, slotId];
    setDraft({ ...draft, slot_ids: next });
  };

  const required = task.total_rounds_required || 4;
  const selected = draft.slot_ids.length;
  const meetsMin = selected >= required;

  return (
    <div style={{ ...s.card, opacity: dim ? 0.75 : 1 }}>
      <div style={s.cardHeader}>
        <div>
          <div style={s.cardTag}>📋 AVAILABILITY POLL</div>
          <div style={s.cardTitle}>{task.tournament_name}</div>
        </div>
        <span style={{
          ...s.statusBadge,
          background: task.completed ? '#d4edda' : '#fff3cd',
          color: task.completed ? '#155724' : '#856404',
        }}>
          {task.completed ? '✓ Submitted' : 'Action required'}
        </span>
      </div>
      <div style={s.cardBody}>
        <div style={s.instr}>
          Select your available times below. You need at least <strong>{required}</strong> slot{required !== 1 ? 's' : ''} ({required} round{required !== 1 ? 's' : ''} in this tournament).
        </div>
        <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ flex: 1, height: 6, background: '#e9ecef', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, (selected / required) * 100)}%`, background: meetsMin ? '#28a745' : '#ffc107', borderRadius: 3, transition: 'width 0.2s' }} />
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: meetsMin ? '#155724' : '#856404', whiteSpace: 'nowrap' as const }}>
            {selected} / {required}
          </span>
        </div>
        <PollAvailabilityGrid
          slots={task.slots}
          selectedSlotIds={draft.slot_ids}
          onToggle={toggleSlot}
        />
        <label style={s.notesLabel}>Notes for the organizer (optional)</label>
        <textarea
          style={s.notes}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          rows={2}
          placeholder="e.g. Can only play mornings, need a cart…"
        />
        {!meetsMin && selected > 0 && (
          <div style={{ fontSize: 12, color: '#856404', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4, padding: '6px 10px', marginBottom: 8 }}>
            You have {selected} slot{selected !== 1 ? 's' : ''} selected — you need at least {required} to meet the minimum for this tournament.
          </div>
        )}
        <button style={{ ...s.submitBtn, opacity: meetsMin ? 1 : 0.7 }} onClick={onSubmit} disabled={submitting}>
          {submitting ? 'Saving…' : task.completed ? 'Update my availability' : 'Submit my availability'}
        </button>
      </div>
    </div>
  );
};

/* ─── Styles ──────────────────────────────────────────────────── */

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 780, margin: '0 auto', padding: '20px 16px' },
  title: { margin: '0 0 20px 0', fontSize: 26, fontWeight: 700 },
  loading: { padding: 30, textAlign: 'center', color: '#888' },
  section: { marginBottom: 32 },
  sectionHeader: {
    margin: '0 0 12px 0',
    fontSize: 15,
    fontWeight: 700,
    color: '#212529',
    paddingBottom: 6,
    borderBottom: '2px solid #dee2e6',
  },
  taskGroupLabel: {
    margin: '14px 0 8px 0',
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    color: '#333',
  },
  emptyUpcoming: { fontSize: 13, color: '#888', padding: '12px 0' },
  collapseToggle: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 600, color: '#6c757d',
    padding: '10px 0', textTransform: 'uppercase' as const, letterSpacing: 0.5,
    display: 'flex', alignItems: 'center', gap: 6,
  },
  empty: {
    background: '#fff', border: '1px solid #dee2e6', borderRadius: 8,
    padding: 20, textAlign: 'center', color: '#28a745', fontSize: 14,
  },

  /* Tournament event card */
  eventCard: {
    background: '#fff', border: '1px solid #dee2e6', borderRadius: 10,
    marginBottom: 14, overflow: 'hidden',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  eventCardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '12px 16px', background: '#0d6efd', borderBottom: '1px solid #0b5ed7',
  },
  eventCardTag: { fontSize: 9, fontWeight: 700, letterSpacing: 1, color: 'rgba(255,255,255,0.75)', marginBottom: 2 },
  eventCardTitle: { fontSize: 16, fontWeight: 700, color: '#fff' },
  draftBadge: {
    fontSize: 10, fontWeight: 600, background: 'rgba(255,255,255,0.2)', color: '#fff',
    padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' as const,
  },
  roundsList: { padding: '0' },

  /* Round row */
  roundRow: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    padding: '12px 16px', borderBottom: '1px solid #f1f3f5',
    flexWrap: 'wrap' as const,
  },
  roundDateBlock: { flex: '0 0 auto', minWidth: 110 },
  roundDate: { fontSize: 13, fontWeight: 700, color: '#212529', marginBottom: 4 },
  sessionBadge: {
    display: 'inline-block', fontSize: 11, fontWeight: 700,
    padding: '2px 8px', borderRadius: 4,
  },
  roundMeta: { flex: '1 1 140px', minWidth: 0 },
  courseName: { fontSize: 13, color: '#495057', marginBottom: 3 },
  teeTimeConfirmed: { fontSize: 13, fontWeight: 600, color: '#155724' },
  teeTimeTbd: { fontSize: 12, color: '#adb5bd', fontStyle: 'italic' },
  reservationVenue: { fontSize: 11, color: '#6c757d', marginTop: 2 },
  roundFlags: { display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' as const },
  flag: { fontSize: 11, color: '#856404' },
  roundGroupBlock: { flex: '0 0 auto', textAlign: 'right' as const },
  groupLabel: { fontSize: 11, fontWeight: 700, color: '#0d6efd', marginBottom: 3 },
  groupMember: { fontSize: 12, color: '#6c757d' },
  groupMemberSelf: { fontSize: 12, color: '#0d6efd', fontWeight: 700 },

  /* Poll task card */
  card: {
    background: '#fff', border: '1px solid #dee2e6', borderRadius: 8,
    marginBottom: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.04)', overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '12px 16px', borderBottom: '1px solid #f1f3f5', background: '#fafbfc',
  },
  cardTag: { fontSize: 10, fontWeight: 600, color: '#0d6efd', letterSpacing: 0.8, marginBottom: 2 },
  cardTitle: { fontSize: 16, fontWeight: 600, color: '#212529' },
  statusBadge: { fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 12 },
  cardBody: { padding: 14 },
  instr: { fontSize: 13, color: '#495057', marginBottom: 10 },
  notesLabel: { display: 'block', fontSize: 12, color: '#495057', marginBottom: 4, marginTop: 12 },
  notes: {
    width: '100%', fontSize: 13, padding: 8,
    border: '1px solid #ced4da', borderRadius: 4,
    boxSizing: 'border-box' as const, resize: 'vertical' as const, marginBottom: 12,
  },
  submitBtn: {
    background: '#0d6efd', color: '#fff', border: 'none',
    borderRadius: 4, padding: '8px 18px', cursor: 'pointer',
    fontSize: 14, fontWeight: 500,
  },
};

export default MyCareer;
